import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import anime from "animejs";
import Shuffle from "shufflejs"; // Import Shuffle
import Navbar from "../components/Navbar";
import profile from "../assets/images/profile.png";
import { useNavigate } from "react-router-dom";
import { transformStrapiArticle, getStrapiURL } from "../utils/strapiUtils";
import SEO from "../components/SEO";

// Helper function to format date
const formatDate = (dateString) => {
  if (!dateString) return "Date unknown";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch (e) {
    return "Date invalid";
  }
};

const Home = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [tagSearch, setTagSearch] = useState("");
  const [limit, setLimit] = useState(15);
  const [page, setPage] = useState(1);
  const [totalPosts, setTotalPosts] = useState(0);
  const [accumulatedPosts, setAccumulatedPosts] = useState([]);
  const [isLoadMoreMode, setIsLoadMoreMode] = useState(false);

  // Refs
  const cardObserver = useRef(null);
  const containerRef = useRef(null);
  const shuffleRef = useRef(null); // Ref for Shuffle instance

  // Fetch Data
  useEffect(() => {
    const getAllBloges = async () => {
      setIsLoading(true);
      const url = getStrapiURL("/api/articles");

      try {
        const { data } = await axios.get(url, {
          params: {
            "pagination[page]": page,
            "pagination[pageSize]": limit,
            "filters[tags][name][$contains]": tagSearch || undefined,
            populate: "*",
            sort: "createdAt:desc",
          },
        });

        if (data.data) {
          const transformedPosts = data.data.map(transformStrapiArticle);
          if (isLoadMoreMode) {
            setAccumulatedPosts((prev) => [...prev, ...transformedPosts]);
          } else {
            setAccumulatedPosts(transformedPosts);
          }
          setPosts(transformedPosts);
          setTotalPosts(data.meta?.pagination?.total || 0);
        } else {
          setPosts([]);
          setAccumulatedPosts([]);
          setTotalPosts(0);
        }
      } catch (error) {
        console.error("Error fetching posts:", error);
        setPosts([]);
        setAccumulatedPosts([]);
        setTotalPosts(0);
      } finally {
        setIsLoading(false);
        setIsLoadMoreMode(false);
      }
    };
    getAllBloges();
  }, [page, limit, tagSearch]);

  // Initialize and Update Shuffle.js
  useEffect(() => {
    // Only initialize if the container exists and we have posts
    if (containerRef.current && accumulatedPosts.length > 0) {
      if (!shuffleRef.current) {
        // Initialize Shuffle
        shuffleRef.current = new Shuffle(containerRef.current, {
          itemSelector: ".masonry-grid-item",
          sizer: ".masonry-grid-item", // Use the item itself as the sizer (or a dedicated element)
          speed: 500, // Animation speed
        });
      } else {
        // If instance exists, reset it to recognize new DOM elements
        shuffleRef.current.resetItems();
      }
    }

    // Cleanup on unmount
    return () => {
      if (shuffleRef.current) {
        shuffleRef.current.destroy();
        shuffleRef.current = null;
      }
    };
  }, [accumulatedPosts]); // Re-run when posts change

  // Animation Observer
  useEffect(() => {
    if (!cardObserver.current) {
      cardObserver.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const cardArticle = entry.target; // This is the <article> element

            // Check if intersecting and not already animated
            if (entry.isIntersecting && !cardArticle.dataset.animated) {
              cardArticle.dataset.animated = "true";

              // FIX: Animate the inner .card div, not the outer <article> (entry.target)
              // This prevents Anime.js from overriding Shuffle.js transforms
              const innerCard = cardArticle.querySelector(".card");

              if (innerCard) {
                anime({
                  targets: innerCard,
                  translateX: [-50, 0],
                  translateY: [-50, 0],
                  scale: [0.8, 1],
                  rotate: [-10, 0],
                  opacity: [0, 1],
                  duration: 1400,
                  easing: "easeOutExpo",
                  delay: 50,
                });

                anime({
                  targets: innerCard.querySelector(".card-img-top"),
                  scale: [0.7, 1],
                  opacity: [0, 1],
                  duration: 1100,
                  easing: "easeOutExpo",
                  delay: 350,
                });

                anime({
                  targets: innerCard.querySelector(".card-title"),
                  rotateX: [-90, 0],
                  opacity: [0, 1],
                  duration: 1000,
                  easing: "easeOutExpo",
                  delay: 450,
                });

                anime({
                  targets: innerCard.querySelector(".card-text"),
                  scale: [0.8, 1],
                  opacity: [0, 1],
                  duration: 1000,
                  easing: "easeOutElastic(1, .8)",
                  delay: 550,
                });
              }
            }
          });
        },
        { threshold: 0.1 },
      );
    }

    return () => {
      if (cardObserver.current) {
        cardObserver.current.disconnect();
        cardObserver.current = null;
      }
    };
  }, []);

  // Connect observer to new elements
  useEffect(() => {
    if (!cardObserver.current) return;
    const cards = document.querySelectorAll(".masonry-grid-item");
    cards.forEach((card) => {
      if (!card.dataset.animated) {
        cardObserver.current.observe(card);
      }
    });
    // No cleanup needed here as we want to keep observing
  }, [accumulatedPosts]);

  // Page Load Header Animation
  useEffect(() => {
    if (isLoading || accumulatedPosts.length === 0) return;

    anime({
      targets: ".container .row h1",
      translateY: [100, 0],
      opacity: [0, 1],
      duration: 1200,
      easing: "easeOutElastic(1, .8)",
      delay: 200,
    });
    // ... rest of header animations
  }, [isLoading, accumulatedPosts]);

  const handleLimitChange = (e) => {
    setLimit(parseInt(e.target.value));
    setPage(1);
    setIsLoadMoreMode(false);
  };

  const handleSearchChange = (e) => {
    setTagSearch(e.target.value);
    setPage(1);
    setIsLoadMoreMode(false);
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setIsLoadMoreMode(false);
  };

  const handleLoadMore = () => {
    setIsLoadMoreMode(true);
    setPage((prev) => prev + 1);
  };

  const totalPages = Math.ceil(totalPosts / limit);

  // Handler for image load to update grid layout
  const handleImageLoad = () => {
    if (shuffleRef.current) {
      shuffleRef.current.layout();
    }
  };

  return (
    <main className="page-wrapper">
      <SEO
        title="Home - Tech Insights"
        schema={{
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Hiren Hadiya Blogs",
          url: "https://hadiya.in",
          potentialAction: {
            "@type": "SearchAction",
            target: "https://hadiya.in/?search={search_term_string}",
            "query-input": "required name=search_term_string",
          },
        }}
      />
      <Navbar />

      <div className="container pt-5 pb-lg-5 pb-md-4 pb-2 my-5">
        <div className="row align-items-center gy-2 mb-4 pb-1 pb-sm-2 pb-lg-3">
          <div className="col-12">
            <h1 className="mb-lg-0">New Blog</h1>
          </div>
        </div>

        {/* Masonry Grid Container Ref */}
        <div
          className="masonry-grid row mb-2 mb-md-4 pb-lg-3"
          ref={containerRef}
          data-columns="3"
        >
          {isLoading && page === 1 ? (
            <div className="d-flex justify-content-center w-100">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : accumulatedPosts.length === 0 ? (
            <p className="text-center w-100">No posts found.</p>
          ) : (
            accumulatedPosts.map((post, ind) => (
              <article
                key={`${post._id}-${ind}`}
                className="masonry-grid-item col-12 col-sm-6 col-lg-4"
                onClick={() => navigate(`/blog/${post.slug}`)}
                // Style ensure items don't overlap before JS loads
              >
                <div className="card border-0 bg-secondary">
                  {post.content?.[0]?.type === "image" &&
                    post.content[0].value && (
                      <a href={`/blog/${post.slug || post._id}`}>
                        <img
                          className="card-img-top"
                          src={post.content[0].value}
                          alt={post.title}
                          onLoad={handleImageLoad} // Trigger layout update on load
                        />
                      </a>
                    )}
                  <div className="card-body p-4">
                    <div className="d-flex align-items-center mb-4 mt-n1">
                      <span className="fs-sm text-body-secondary">
                        {formatDate(post.createdAt)}
                      </span>
                      {post.tags?.[0] && (
                        <>
                          <span className="fs-xs opacity-20 mx-3">|</span>
                          <a className="badge text-nav fs-xs border" href="#">
                            {post.tags[0]}
                          </a>
                        </>
                      )}
                    </div>
                    <h3 className="h4 card-title">{post.title}</h3>
                    <p className="card-text">{post.description}</p>
                  </div>
                  <div className="card-footer pt-3">
                    <a
                      className="d-flex align-items-center text-decoration-none pb-2"
                      href="#"
                    >
                      <img
                        className="rounded-circle"
                        src={profile}
                        width="48"
                        alt={post.author || "Author"}
                      />
                      <h6 className="ps-3 mb-0">{post.author || "Admin"}</h6>
                    </a>
                  </div>
                </div>
              </article>
            ))
          )}
        </div>

        {/* Pagination Section (Unchanged) */}
        <div className="row gy-3 align-items-center mb-md-2 mb-xl-4">
          {/* ... existing pagination code ... */}
          {/* Included for context but hidden for brevity as it is unchanged */}
          <div className="col-md-4 col-6">{/* Limit selector */}</div>
          <div className="col-md-4 col-12 text-center">
            {accumulatedPosts.length < totalPosts && (
              <button
                className="btn btn-primary"
                onClick={handleLoadMore}
                disabled={isLoading}
              >
                {isLoading ? "Loading..." : "Load more posts"}
              </button>
            )}
          </div>
          {/* Page numbers... */}
        </div>
      </div>
    </main>
  );
};

export default Home;
